import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaTimes, FaSyncAlt, FaFileInvoiceDollar, FaEnvelope,
  FaCheckCircle, FaExclamationTriangle, FaBan, FaRegClock,
  FaFilter, FaSearch, FaQrcode, FaBuilding, FaInbox,
  FaCodeBranch,
} from 'react-icons/fa';
import './BillingMonitorModal.css';

// ── helpers ──────────────────────────────────────────────────────────────────
const moneyBRL = (cents) =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const isoDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return String(d).slice(0, 10);
  return dt.toLocaleDateString('pt-BR');
};

const isoDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return String(d).slice(0, 16);
  return dt.toLocaleString('pt-BR');
};

const INVOICE_STATUS = {
  open:     { label: 'Em Aberto', cls: 'warning' },
  overdue:  { label: 'Vencida',   cls: 'danger'  },
  paid:     { label: 'Paga',      cls: 'success' },
  canceled: { label: 'Cancelada', cls: 'neutral' },
};

const TPL_LABELS = {
  invoice_created: '📄 Fatura Criada',
  pre_due:         '⏰ Pré-Vencimento',
  on_due_date:     '📅 Vencimento Hoje',
  overdue:         '⚠️ Vencida',
  suspended:       '🔒 Suspensão',
  paid:            '✅ Pagamento',
};

const CH_ICON = { email: <FaEnvelope />, sms: '💬' };

// ── sub-componentes ───────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, variant = 'neutral' }) {
  return (
    <div className={`bmm-kpi bmm-kpi--${variant}`}>
      <div className="bmm-kpi__icon">{icon}</div>
      <div className="bmm-kpi__body">
        <div className="bmm-kpi__value">{value}</div>
        <div className="bmm-kpi__label">{label}</div>
        {sub && <div className="bmm-kpi__sub">{sub}</div>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = INVOICE_STATUS[status] || { label: status, cls: 'neutral' };
  return <span className={`bmm-badge bmm-badge--${s.cls}`}>{s.label}</span>;
}

function Skeleton() {
  return (
    <div className="bmm-skeleton-wrap">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="bmm-skeleton-row">
          <div className="bmm-sk bmm-sk--lg" />
          <div className="bmm-sk bmm-sk--md" />
          <div className="bmm-sk bmm-sk--sm" />
          <div className="bmm-sk bmm-sk--sm" />
          <div className="bmm-sk bmm-sk--md" />
        </div>
      ))}
    </div>
  );
}

// ── componente principal ──────────────────────────────────────────────────────
export default function BillingMonitorModal({ onClose, initialClienteId = '', initialClienteNome = '' }) {
  const [tab, setTab] = useState('invoices');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ── Faturas ─────────────────────────────────────────────────────────────
  const [invoices,  setInvoices]  = useState([]);
  const [kpis,      setKpis]      = useState({});
  const [invTotal,  setInvTotal]  = useState(0);
  const [invPage,   setInvPage]   = useState(1);
  const invLimit = 20;

  const [filterStatus,    setFilterStatus]    = useState('');
  // Quando aberto a partir do drawer de um cliente (via "Ver no Monitor"),
  // já chega filtrado nele — evita o usuário ter que buscar de novo.
  const [filterClienteId, setFilterClienteId] = useState(initialClienteId ? String(initialClienteId) : '');
  const [filterQ,         setFilterQ]         = useState(initialClienteNome || '');
  const [filterFrom,      setFilterFrom]      = useState('');
  const [filterTo,        setFilterTo]        = useState('');

  // ── Logs ────────────────────────────────────────────────────────────────
  const [logs,      setLogs]      = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage,  setLogsPage]  = useState(1);
  const logsLimit = 40;

  const [filterChannel, setFilterChannel]     = useState('');
  const [filterTplKey,  setFilterTplKey]       = useState('');
  const [filterLogStatus, setFilterLogStatus] = useState('');

  // ── Webhooks ─────────────────────────────────────────────────────────────
  const [webhooks,  setWebhooks]  = useState([]);
  const [wbTotal,   setWbTotal]   = useState(0);
  const [wbPage,    setWbPage]    = useState(1);
  const wbLimit = 20;
  const [expandedWbId, setExpandedWbId] = useState(null);

  // ── Fetch faturas ─────────────────────────────────────────────────────────
  const fetchInvoices = useCallback(async ({ soft = false } = {}) => {
    if (!soft) setLoading(true);
    setRefreshing(true);
    try {
      const { data } = await api.get('/api/system-hub/billing/monitor', {
        params: {
          page: invPage, limit: invLimit,
          status: filterStatus || undefined,
          cliente_id: filterClienteId || undefined,
          q: filterQ || undefined,
          from: filterFrom || undefined,
          to: filterTo || undefined,
        },
      });
      setInvoices(Array.isArray(data?.invoices) ? data.invoices : []);
      setKpis(data?.kpis || {});
      setInvTotal(Number(data?.total || 0));
    } catch {
      toast.error('Falha ao carregar monitor.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [invPage, filterStatus, filterClienteId, filterQ, filterFrom, filterTo]);

  // ── Fetch logs ────────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/system-hub/billing/monitor/logs', {
        params: {
          page: logsPage, limit: logsLimit,
          channel: filterChannel || undefined,
          template_key: filterTplKey || undefined,
          status: filterLogStatus || undefined,
        },
      });
      setLogs(Array.isArray(data?.logs) ? data.logs : []);
      setLogsTotal(Number(data?.total || 0));
    } catch {
      toast.error('Falha ao carregar logs.');
    } finally {
      setLoading(false);
    }
  }, [logsPage, filterChannel, filterTplKey, filterLogStatus]);

  // ── Fetch webhooks ────────────────────────────────────────────────────────
  const fetchWebhooks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/system-hub/billing/monitor/webhooks', {
        params: { page: wbPage, limit: wbLimit },
      });
      setWebhooks(Array.isArray(data?.webhooks) ? data.webhooks : []);
      setWbTotal(Number(data?.total || 0));
    } catch {
      toast.error('Falha ao carregar webhooks.');
    } finally {
      setLoading(false);
    }
  }, [wbPage]);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (tab === 'invoices') fetchInvoices();
    if (tab === 'logs')     fetchLogs();
    if (tab === 'webhooks') fetchWebhooks();
  }, [tab, fetchInvoices, fetchLogs, fetchWebhooks]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const invTotalPages = Math.max(1, Math.ceil(invTotal  / invLimit));
  const logsTotalPages = Math.max(1, Math.ceil(logsTotal / logsLimit));
  const wbTotalPages  = Math.max(1, Math.ceil(wbTotal   / wbLimit));

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="bmm-backdrop" onMouseDown={onClose}>
      <div className="bmm-panel" onMouseDown={(e) => e.stopPropagation()}>

        {/* ── HEADER ──────────────────────────────────────────────────── */}
        <div className="bmm-header">
          <div className="bmm-header__left">
            <FaFileInvoiceDollar className="bmm-header__icon" />
            <div>
              <h2 className="bmm-header__title">Monitor de Cobrança</h2>
              <p className="bmm-header__sub">
                Transparência total: faturas, comunicações, webhooks e pagamentos.
              </p>
            </div>
          </div>
          <div className="bmm-header__actions">
            <button
              className="bmm-btn bmm-btn--ghost"
              onClick={() => {
                if (tab === 'invoices') fetchInvoices({ soft: true });
                if (tab === 'logs')     fetchLogs();
                if (tab === 'webhooks') fetchWebhooks();
              }}
              disabled={refreshing}
              title="Atualizar"
            >
              <FaSyncAlt className={refreshing ? 'bmm-spin' : ''} />
            </button>
            <button className="bmm-btn bmm-btn--ghost bmm-btn--icon" onClick={onClose} title="Fechar">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* ── KPIs ────────────────────────────────────────────────────── */}
        <div className="bmm-kpis">
          <KpiCard icon={<FaFileInvoiceDollar />} label="Total"    value={String(kpis.total || 0)}   variant="neutral" />
          <KpiCard icon={<FaCheckCircle />}       label="Pagas"    value={String(kpis.paid  || 0)}   variant="success"
            sub={moneyBRL(kpis.total_paid_cents)} />
          <KpiCard icon={<FaRegClock />}          label="Em Aberto" value={String(kpis.open || 0)}    variant="warning"
            sub={moneyBRL(kpis.total_open_cents)} />
          <KpiCard icon={<FaExclamationTriangle />} label="Vencidas" value={String(kpis.overdue || 0)} variant="danger" />
          <KpiCard icon={<FaBan />}               label="Canceladas" value={String(kpis.canceled || 0)} variant="neutral" />
        </div>

        {/* ── TABS ────────────────────────────────────────────────────── */}
        <div className="bmm-tabs">
          {[
            { key: 'invoices', label: 'Faturas',       icon: <FaFileInvoiceDollar /> },
            { key: 'logs',     label: 'Comunicações',  icon: <FaEnvelope /> },
            { key: 'webhooks', label: 'Webhooks',      icon: <FaCodeBranch /> },
          ].map((t) => (
            <button
              key={t.key}
              className={`bmm-tab ${tab === t.key ? 'active' : ''}`}
              onClick={() => { setTab(t.key); setLoading(true); }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ── CONTENT ─────────────────────────────────────────────────── */}
        <div className="bmm-body">

          {/* ── ABA: FATURAS ────────────────────────────────────────── */}
          {tab === 'invoices' && (
            <>
              {/* Filtros */}
              <div className="bmm-filters">
                {filterClienteId && (
                  <span className="bmm-tag bmm-tag--blue bmm-tag--clientfilter">
                    <FaBuilding /> {filterQ || `Cliente #${filterClienteId}`}
                    <button
                      type="button"
                      className="bmm-tag__clear"
                      onClick={() => { setFilterClienteId(''); setFilterQ(''); setInvPage(1); }}
                      title="Limpar filtro de cliente"
                    >
                      <FaTimes />
                    </button>
                  </span>
                )}
                <div className="bmm-search">
                  <FaSearch />
                  <input
                    value={filterQ}
                    onChange={(e) => { setFilterQ(e.target.value); setInvPage(1); }}
                    placeholder="Cliente..."
                  />
                </div>
                <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setInvPage(1); }}>
                  <option value="">Todos os status</option>
                  <option value="open">Em Aberto</option>
                  <option value="overdue">Vencidas</option>
                  <option value="paid">Pagas</option>
                  <option value="canceled">Canceladas</option>
                </select>
                <input type="date" value={filterFrom} onChange={(e) => { setFilterFrom(e.target.value); setInvPage(1); }} title="De" />
                <input type="date" value={filterTo}   onChange={(e) => { setFilterTo(e.target.value);   setInvPage(1); }} title="Até" />
                <button className="bmm-btn bmm-btn--primary" onClick={() => fetchInvoices()}>
                  <FaFilter /> Filtrar
                </button>
              </div>

              {loading ? <Skeleton /> : (
                <>
                  <div className="bmm-table">
                    <div className="bmm-table__head">
                      <div>Cliente</div>
                      <div>Competência</div>
                      <div>Valor</div>
                      <div>Vencimento</div>
                      <div>Atraso</div>
                      <div>Status</div>
                      <div>Método</div>
                      <div>Msgs</div>
                      <div>Último envio</div>
                    </div>
                    <div className="bmm-table__body">
                      {invoices.length === 0 ? (
                        <div className="bmm-empty">
                          <FaInbox size={32} />
                          <p>Nenhuma fatura encontrada com esses filtros.</p>
                        </div>
                      ) : invoices.map((inv) => (
                        <div
                          key={inv.id}
                          className={`bmm-table__row ${inv.status === 'overdue' ? 'bmm-row--overdue' : ''}`}
                        >
                          <div className="bmm-cell">
                            <FaBuilding className="bmm-cell__icon" />
                            <div>
                              <strong>{inv.nome_empresa}</strong>
                              <span className="bmm-muted"> #{inv.id}</span>
                            </div>
                          </div>
                          <div className="bmm-cell bmm-mono">{inv.competencia}</div>
                          <div className="bmm-cell bmm-bold">{moneyBRL(inv.amount_cents)}</div>
                          <div className="bmm-cell">{isoDate(inv.due_date)}</div>
                          <div className="bmm-cell">
                            {Number(inv.days_overdue) > 0
                              ? <span className="bmm-badge bmm-badge--danger">{inv.days_overdue}d</span>
                              : <span className="bmm-badge bmm-badge--success">OK</span>}
                          </div>
                          <div className="bmm-cell"><StatusBadge status={inv.status} /></div>
                          <div className="bmm-cell">
                            {inv.payment_method === 'pix'
                              ? <><FaQrcode /> PIX</>
                              : inv.payment_method === 'boleto'
                              ? <><FaFileInvoiceDollar /> Boleto</>
                              : <span className="bmm-muted">—</span>}
                          </div>
                          <div className="bmm-cell bmm-mono">{inv.msg_count || 0}</div>
                          <div className="bmm-cell bmm-muted">{isoDateTime(inv.last_sent_at)}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bmm-pagination">
                    <button className="bmm-btn bmm-btn--ghost" disabled={invPage <= 1} onClick={() => setInvPage((p) => p - 1)}>← Anterior</button>
                    <span className="bmm-muted">Página <b>{invPage}</b> de <b>{invTotalPages}</b> · {invTotal} faturas</span>
                    <button className="bmm-btn bmm-btn--ghost" disabled={invPage >= invTotalPages} onClick={() => setInvPage((p) => p + 1)}>Próxima →</button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── ABA: COMUNICAÇÕES ───────────────────────────────────── */}
          {tab === 'logs' && (
            <>
              <div className="bmm-filters">
                <select value={filterChannel} onChange={(e) => { setFilterChannel(e.target.value); setLogsPage(1); }}>
                  <option value="">Canal: todos</option>
                  <option value="email">E-mail</option>
                  <option value="sms">SMS</option>
                </select>
                <select value={filterTplKey} onChange={(e) => { setFilterTplKey(e.target.value); setLogsPage(1); }}>
                  <option value="">Tipo: todos</option>
                  {Object.entries(TPL_LABELS).map(([k, l]) => (
                    <option key={k} value={k}>{l}</option>
                  ))}
                </select>
                <select value={filterLogStatus} onChange={(e) => { setFilterLogStatus(e.target.value); setLogsPage(1); }}>
                  <option value="">Status: todos</option>
                  <option value="sent">Enviados</option>
                  <option value="failed">Falhas</option>
                  <option value="queued">Na fila</option>
                </select>
                <button className="bmm-btn bmm-btn--primary" onClick={fetchLogs}>
                  <FaFilter /> Filtrar
                </button>
              </div>

              {loading ? <Skeleton /> : (
                <>
                  <div className="bmm-timeline">
                    {logs.length === 0 ? (
                      <div className="bmm-empty">
                        <FaEnvelope size={32} />
                        <p>Nenhuma comunicação encontrada.</p>
                      </div>
                    ) : logs.map((log) => (
                      <div key={log.id} className="bmm-tl-item">
                        <div className={`bmm-tl-dot bmm-tl-dot--${log.status === 'sent' ? 'success' : log.status === 'failed' ? 'danger' : 'neutral'}`} />
                        <div className="bmm-tl-content">
                          <div className="bmm-tl-top">
                            <div className="bmm-tl-meta">
                              <span className="bmm-tag">{CH_ICON[log.channel]} {log.channel}</span>
                              <span className="bmm-tag bmm-tag--blue">{TPL_LABELS[log.template_key] || log.template_key}</span>
                              {log.invoice_id && <span className="bmm-muted">inv #{log.invoice_id}</span>}
                            </div>
                            <span className={`bmm-badge bmm-badge--${log.status === 'sent' ? 'success' : log.status === 'failed' ? 'danger' : 'neutral'}`}>
                              {log.status === 'sent' ? '✓ Enviado' : log.status === 'failed' ? '✗ Falhou' : 'Na fila'}
                            </span>
                          </div>
                          <div className="bmm-tl-client">
                            <strong>{log.nome_empresa || `Cliente #${log.cliente_id}`}</strong>
                            <span className="bmm-muted"> · {log.to_email || log.to_phone || '—'}</span>
                          </div>
                          {log.subject && <div className="bmm-tl-subject">{log.subject}</div>}
                          <div className="bmm-tl-time">{isoDateTime(log.sent_at || log.created_at)}</div>
                          {log.error_message && (
                            <div className="bmm-tl-error">{String(log.error_message).slice(0, 200)}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bmm-pagination">
                    <button className="bmm-btn bmm-btn--ghost" disabled={logsPage <= 1} onClick={() => setLogsPage((p) => p - 1)}>← Anterior</button>
                    <span className="bmm-muted">Página <b>{logsPage}</b> de <b>{logsTotalPages}</b> · {logsTotal} registros</span>
                    <button className="bmm-btn bmm-btn--ghost" disabled={logsPage >= logsTotalPages} onClick={() => setLogsPage((p) => p + 1)}>Próxima →</button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── ABA: WEBHOOKS ───────────────────────────────────────── */}
          {tab === 'webhooks' && (
            <>
              {loading ? <Skeleton /> : (
                <>
                  <div className="bmm-table">
                    <div className="bmm-table__head bmm-table__head--wb">
                      <div>Data</div>
                      <div>Evento</div>
                      <div>Charge ID</div>
                      <div>Charge Status</div>
                      <div>Processado</div>
                    </div>
                    <div className="bmm-table__body">
                      {webhooks.length === 0 ? (
                        <div className="bmm-empty">
                          <FaCodeBranch size={32} />
                          <p>Nenhum webhook recebido ainda.</p>
                        </div>
                      ) : webhooks.map((wh) => (
                        <div key={wh.id} className="bmm-table__row bmm-table__row--wb">
                          <div className="bmm-cell bmm-muted">{isoDateTime(wh.created_at)}</div>
                          <div className="bmm-cell">
                            <span className="bmm-tag bmm-tag--blue">{wh.event_type || '—'}</span>
                          </div>
                          <div className="bmm-cell bmm-mono">{wh.charge_id || '—'}</div>
                          <div className="bmm-cell">
                            {wh.charge_status
                              ? <span className={`bmm-badge bmm-badge--${wh.charge_status === 'RECEIVED' || wh.charge_status === 'CONFIRMED' ? 'success' : 'neutral'}`}>{wh.charge_status}</span>
                              : <span className="bmm-muted">—</span>}
                          </div>
                          <div className="bmm-cell">
                            {wh.processed
                              ? <span className="bmm-badge bmm-badge--success">✓</span>
                              : <span className="bmm-badge bmm-badge--warning">Pendente</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bmm-pagination">
                    <button className="bmm-btn bmm-btn--ghost" disabled={wbPage <= 1} onClick={() => setWbPage((p) => p - 1)}>← Anterior</button>
                    <span className="bmm-muted">Página <b>{wbPage}</b> de <b>{wbTotalPages}</b> · {wbTotal} webhooks</span>
                    <button className="bmm-btn bmm-btn--ghost" disabled={wbPage >= wbTotalPages} onClick={() => setWbPage((p) => p + 1)}>Próxima →</button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}